import { useState } from 'react';
import { Plane, TrainFront, FileText, ExternalLink, CheckCircle2 } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import { post } from '../../api/client';
import { Card, Badge, StatusBadge, Loader, EmptyState, SectionTitle } from '../../components/UI';
import { fmtDate, fmtTime } from '../../utils/format';
import { NotIncluded, chipStyle } from './shared';

const RETURN_MODES = [
  { value: 'hotel', label: 'From the hotel' },
  { value: 'airport', label: 'Direct to airport' },
  { value: 'self', label: 'Own transport' },
];

function FlightCard({ flight }) {
  const docs = flight.tickets?.length
    ? flight.tickets
    : (flight.ticketUrl ? [{ url: flight.ticketUrl, fileName: flight.ticketFileName }] : []);

  return (
    <Card padding={20}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontSize: 15, fontWeight: 600 }}>{[flight.airline, flight.mode === 'train' ? `Train ${flight.flightNumber || ''}`.trim() : flight.flightNumber, flight.travelClass].filter(Boolean).join(' · ') || (flight.mode === 'train' ? 'Train' : 'Flight')}</p>
          {flight.pnr && <p style={{ fontSize: 12.5, color: 'var(--gold-400)', marginTop: 2 }}>PNR {flight.pnr}</p>}
        </div>
        <StatusBadge status={flight.status} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <div>
          <p style={{ fontSize: 22, fontWeight: 700 }}>{flight.from?.code || '—'}</p>
          <p style={{ fontSize: 12, color: 'var(--text-lo)' }}>{flight.from?.city}</p>
          {flight.departureAt && <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 4 }}>{fmtTime(flight.departureAt)}</p>}
        </div>
        {flight.mode === 'train' ? <TrainFront size={18} color="var(--gold-500)" /> : <Plane size={18} color="var(--gold-500)" />}
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 22, fontWeight: 700 }}>{flight.to?.code || '—'}</p>
          <p style={{ fontSize: 12, color: 'var(--text-lo)' }}>{flight.to?.city}</p>
          {flight.arrivalAt && <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 4 }}>{fmtTime(flight.arrivalAt)}</p>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 14, fontSize: 12.5, color: 'var(--text-mid)' }}>
        <span>Date: <strong style={{ color: 'var(--text-hi)' }}>{flight.departureAt ? fmtDate(flight.departureAt) : 'TBA'}</strong></span>
        {flight.seatNumber && <span>Seat: <strong style={{ color: 'var(--text-hi)' }}>{flight.seatNumber}</strong></span>}
        {flight.baggageAllowance && <span>Baggage: <strong style={{ color: 'var(--text-hi)' }}>{flight.baggageAllowance}</strong></span>}
      </div>

      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--ink-750)' }}>
        {docs.length ? docs.map((d) => (
          <a key={d.url} href={d.url} target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 0', fontSize: 13, color: 'var(--gold-300)' }}>
            <FileText size={14} /> <span style={{ flex: 1 }}>{d.fileName || 'View e-ticket'}</span> <ExternalLink size={13} />
          </a>
        )) : <p style={{ fontSize: 12.5, color: 'var(--text-lo)' }}>Ticket not issued yet</p>}
      </div>
    </Card>
  );
}

export default function AttendeeTravel() {
  const { eventId, hasModule } = useEvents();
  const toast = useToast();
  const includesFlights = hasModule('flights');
  const { data, loading, reload } = useFetch('/flights/my', { event: eventId }, { skip: !eventId || !includesFlights });
  const [saving, setSaving] = useState(false);

  if (!includesFlights) return <><SectionTitle title="Travel" /><NotIncluded what="Flights" icon={Plane} /></>;
  if (loading) return <Loader full label="Loading your travel" />;

  const setReturn = async (mode) => {
    if (!data?.return?._id) return toast.error('Your return journey has not been booked yet');
    setSaving(true);
    try {
      const res = await post(`/flights/${data.return._id}/return-preference`, { returnMode: mode });
      toast.success(res.message);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const legs = [['Onward journey', data?.onward, 'Your onward flight appears here once the organiser issues your ticket.'],
    ['Return journey', data?.return, 'Your return flight appears here once it is confirmed.']];

  return (
    <>
      <SectionTitle title="Travel" subtitle="Your tickets and return journey" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        {legs.map(([label, flight, hint]) => (
          <div key={label}>
            <p style={{ fontSize: 11.5, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600, marginBottom: 10 }}>{label}</p>
            {flight ? <FlightCard flight={flight} /> : <EmptyState message="Tickets not assigned yet" hint={hint} icon={Plane} />}
          </div>
        ))}
      </div>

      {data?.return && (
        <Card padding={20} style={{ marginTop: 16, maxWidth: 640 }}>
          <p style={{ fontSize: 15, fontWeight: 600 }}>How are you heading back?</p>
          <p style={{ fontSize: 12.8, color: 'var(--text-mid)', marginTop: 3 }}>Tell us so we can plan your airport transfer.</p>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 12 }}>
            {RETURN_MODES.map((m) => (
              <button key={m.value} type="button" disabled={saving} style={chipStyle(data.return.returnMode === m.value)} onClick={() => setReturn(m.value)}>
                {m.label}
              </button>
            ))}
          </div>
          {data.return.returnMode && (
            <Badge tone="success" style={{ marginTop: 11 }}>
              <CheckCircle2 size={10} /> {RETURN_MODES.find((m) => m.value === data.return.returnMode)?.label} — noted
            </Badge>
          )}
        </Card>
      )}
    </>
  );
}
